import { useEffect, useRef, useState } from "react";
import { Card, CardContent, CircularProgress, MenuItem, Select, Tab, Tabs } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useTicketsTopicsStats } from "hooks/useTicketsTopicsStats";
import { useBranches } from "hooks/useBranches";
import { TicketsTopic, TicketTypeFilter } from "interfaces/entities/ticketStats";
import { buildTicketsHref, toDateOnly } from "helpers/ticketsHref";

const PALETTE = [
  "#4F46E5",
  "#22C55E",
  "#F59E0B",
  "#EF4444",
  "#06B6D4",
  "#A855F7",
  "#EC4899",
  "#84CC16",
  "#F97316",
  "#0EA5E9",
];
const COLOR_OTHER = "#94A3B8"; // «Прочие» — техническая группа
const COLOR_NO_TOPIC = "#CBD5E1"; // «Без тематики» — техническая группа

function colorForTopic(t: TicketsTopic, idx: number): string {
  if (t.name === "Прочие") return COLOR_OTHER;
  if (t.name === "Без тематики") return COLOR_NO_TOPIC;
  return PALETTE[idx % PALETTE.length];
}

// Плагин для надписи totalCount в центре donut
const buildCenterTextPlugin = (totalLabel: string, totalValue: number) => ({
  id: "centerText",
  afterDraw: (chart: any) => {
    const { ctx, chartArea } = chart;
    if (!chartArea) return;
    const { left, right, top, bottom } = chartArea;
    const cx = (left + right) / 2;
    const cy = (top + bottom) / 2;

    ctx.save();
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";

    ctx.fillStyle = "#0F172A";
    ctx.font = "bold 22px sans-serif";
    ctx.fillText(String(totalValue), cx, cy - 8);

    ctx.fillStyle = "#64748B";
    ctx.font = "12px sans-serif";
    ctx.fillText(totalLabel, cx, cy + 14);

    ctx.restore();
  },
});

type TabValue = "ALL" | TicketTypeFilter;

interface TicketsTopicsPieProps {
  /** Фиксированный тип тикета. Если задан — табы скрываются. */
  type?: TicketTypeFilter;
  /** Период (ISO date YYYY-MM-DD), бэк сам приведёт к startOf/endOfDay */
  from?: string;
  to?: string;
  /** Год — альтернатива from/to (см. бэкенд) */
  year?: number;
  /** Лимит верхних тематик (0 = без свёртки в «Прочие») */
  limit?: number;
  /** Заголовок карточки. По умолчанию вычисляется по типу. */
  title?: string;
  branchID?: number | string;
}

export const TicketsTopicsPie = ({
  type,
  from,
  to,
  year,
  limit,
  title,
  branchID,
}: TicketsTopicsPieProps) => {
  const { t } = useTranslation();

  const [tab, setTab] = useState<TabValue>(type ?? "ALL");
  const effectiveType: TicketTypeFilter | undefined = type ?? (tab === "ALL" ? undefined : tab);

  const [localBranchID, setLocalBranchID] = useState<number | "">("");
  const { data: branches } = useBranches();
  const activeBranchID = localBranchID !== "" ? localBranchID : branchID;

  const { data, isLoading, error } = useTicketsTopicsStats({
    type: effectiveType,
    from,
    to,
    year,
    limit,
    ...(activeBranchID ? { branchID: activeBranchID } : {}),
  });

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<any>(null);

  const navigate = useNavigate();
  const goToTopic = (topic: TicketsTopic) => {
    // «Без тематики» / «Прочие» имеют categoryID=null — фильтр по конкретной тематике
    // не построить (бэкенд не поддерживает IS NULL для categoryID).
    if (!topic.categoryID) return;
    const dateFrom = data ? toDateOnly(data.from) : "";
    const dateTo = data ? toDateOnly(data.to) : "";
    navigate(
      buildTicketsHref({
        categoryID: topic.categoryID,
        type: effectiveType,
        period: dateFrom || dateTo ? "custom" : undefined,
        from: dateFrom,
        to: dateTo,
        branchID: activeBranchID ? String(activeBranchID) : undefined,
      }),
    );
  };

  useEffect(() => {
    const ChartCtor = (window as any).Chart;
    if (!data || !canvasRef.current || !ChartCtor) return;
    if (data.totalCount === 0) return;

    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const topics: TicketsTopic[] = data.topics;

    chartRef.current = new ChartCtor(canvasRef.current, {
      type: "doughnut",
      data: {
        labels: topics.map((t) => t.name),
        datasets: [
          {
            data: topics.map((t) => t.count),
            backgroundColor: topics.map((t, i) => colorForTopic(t, i)),
            borderWidth: 1,
            borderColor: "#fff",
            // небольшой зазор между секторами для читаемости
            spacing: 2,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutout: "60%",
        onHover: (_evt: any, els: any[], chart: any) => {
          const idx = els?.[0]?.index;
          const topic = idx != null ? topics[idx] : undefined;
          chart.canvas.style.cursor = topic?.categoryID ? "pointer" : "default";
        },
        onClick: (_evt: any, els: any[]) => {
          const idx = els?.[0]?.index;
          const topic = idx != null ? topics[idx] : undefined;
          if (topic) goToTopic(topic);
        },
        plugins: {
          legend: {
            display: true,
            position: "right",
            labels: {
              generateLabels: (chart: any) => {
                const ds = chart.data.datasets?.[0];
                if (!ds) return [];
                return topics.map((topic, i) => ({
                  text: `${topic.name} — ${topic.count}`,
                  fillStyle: ds.backgroundColor[i],
                  strokeStyle: ds.backgroundColor[i],
                  hidden: false,
                  index: i,
                }));
              },
              boxWidth: 12,
              boxHeight: 12,
            },
          },
          tooltip: {
            callbacks: {
              title: (items: any[]) => {
                if (!items?.length) return "";
                const topic = topics[items[0].dataIndex];
                return topic?.name || "";
              },
              label: (item: any) => {
                const topic = topics[item.dataIndex];
                if (!topic) return "";
                return ` ${topic.count} шт. (${topic.percent}%)`;
              },
            },
          },
        },
      },
      plugins: [buildCenterTextPlugin("Всего", data.totalCount)],
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [data]);

  const headerTitle =
    title ??
    (effectiveType === "COMPLAINT"
      ? "Жалобы по тематикам"
      : effectiveType === "SUGGESTION"
        ? "Предложения по тематикам"
        : effectiveType === "THANKS"
          ? "Благодарности по тематикам"
          : effectiveType === "OTHER"
            ? "Прочие обращения по тематикам"
            : "Обращения по тематикам");

  return (
    <Card>
      <CardContent>
        <div className="flex flex-space-between items-center mb-2">
          <div>
            <h4 className="text-md font-bold mb-1">{headerTitle}</h4>
            {data && <div className="text-sm color-gray">Всего: {data.totalCount}</div>}
          </div>
          <Select
            size="small"
            value={localBranchID}
            onChange={(e) => setLocalBranchID(e.target.value as number | "")}
            displayEmpty
            style={{ minWidth: 160 }}
          >
            <MenuItem value="">Все филиалы</MenuItem>
            {branches?.data?.map((b: any) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </Select>
        </div>

        {/* Переключатель типа: показываем только если type не задан снаружи */}
        {!type && (
          <Tabs
            value={tab}
            onChange={(_e, v) => setTab(v as TabValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ minHeight: 36, mb: 1 }}
          >
            <Tab sx={{ minHeight: 36 }} value="ALL" label="Все" />
            <Tab sx={{ minHeight: 36 }} value="COMPLAINT" label={t("ticket.type.COMPLAINT")} />
            <Tab sx={{ minHeight: 36 }} value="SUGGESTION" label={t("ticket.type.SUGGESTION")} />
            <Tab sx={{ minHeight: 36 }} value="THANKS" label={t("ticket.type.THANKS")} />
            <Tab sx={{ minHeight: 36 }} value="OTHER" label={t("ticket.type.OTHER")} />
          </Tabs>
        )}

        {isLoading && (
          <div
            className="text-center"
            style={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={28} />
          </div>
        )}

        {!isLoading && error && (
          <div
            className="text-center"
            style={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            Не удалось загрузить статистику
          </div>
        )}

        {!isLoading && !error && data && data.totalCount === 0 && (
          <div
            className="text-center color-gray"
            style={{
              height: 320,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            За выбранный период нет обращений
          </div>
        )}

        {!isLoading && !error && data && data.totalCount > 0 && (
          <div style={{ height: 320, position: "relative" }}>
            <canvas ref={canvasRef} />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default TicketsTopicsPie;
